/**
 * Offline Subscription Service
 * Handles subscription validation both online and offline
 */
import subscriptionStorage from './storage';
import { validateLicenseToken } from './jwtUtils';
import API from './api';
import { getCurrentUserFromStore } from './auth';
import { getDeviceId } from './deviceIdentity';

// Configuration
const SYNC_INTERVAL = 4 * 60 * 60 * 1000; // 4 hours
const GRACE_PERIOD_DAYS = 7;

class OfflineSubscriptionService {
    constructor() {
        this.syncIntervals = new Map(); // Track sync intervals per extension
        this.storeInstance = null;
    }

    /**
     * Set Redux store instance
     */
    setStore(store) {
        this.storeInstance = store;
    }

    /**
     * Get current user from store
     */
    getCurrentUser() {
        if (!this.storeInstance) {
            return null;
        }
        return getCurrentUserFromStore(this.storeInstance);
    }

    /**
     * Check if online
     * Enhanced for desktop apps (Electron)
     */
    isOnline() {
        // Check navigator.onLine (works in both web and Electron)
        if (navigator.onLine !== false) {
            // For desktop apps, we might want additional checks
            // Electron's navigator.onLine can be unreliable, so we can add more checks
            if (window.bodapro && window.bodapro.platformType === 'desktop') {
                // In desktop, we can also check if we can reach the API
                // For now, trust navigator.onLine but could enhance with actual network check
                return true;
            }
            return true;
        }
        return false;
    }

    /**
     * Check entitlements (online or offline)
     * @param {string} extensionKey - Extension identifier
     * @param {string} featureKey - Optional feature key
     * @param {number} quantity - Quantity to check (default: 1)
     * @returns {Promise<Object>} Entitlement check result
     */
    async checkEntitlements(extensionKey, featureKey = '', quantity = 1) {
        // Try online first if available
        if (this.isOnline()) {
            try {
                const result = await this.checkOnline(extensionKey, featureKey, quantity);
                // Cache the result
                await this.cacheLicense(extensionKey, result);
                return result;
            } catch (error) {
                // A live 404 here is the server authoritatively saying there is
                // no active subscription for this user/extension. That answer
                // must win over a possibly-stale offline cache (e.g. left over
                // from a subscription that has since been canceled/expired) —
                // falling back to checkOffline() here would silently mask a
                // real "not subscribed" result with a stale "allowed: true".
                // Only genuine network failures (no HTTP response at all, so
                // no error.status) should fall back to the cache.
                if (error.status === 404) {
                    return {
                        has_subscription: false,
                        subscription_status: null,
                        allowed: false,
                        reason: 'no_active_subscription',
                        offline: false
                    };
                }
                console.warn('Online check failed, using offline cache:', error);
            }
        }

        // Use offline cache
        return this.checkOffline(extensionKey, featureKey, quantity);
    }

    /**
     * Check entitlements online
     */
    async checkOnline(extensionKey, featureKey, quantity) {
        const user = this.getCurrentUser();
        if (!user) {
            throw new Error('User not authenticated');
        }

        // Check entitlements via API
        const result = await API.checkEntitlements(
            user.id,
            extensionKey,
            featureKey,
            quantity
        );

        // Fetch and cache license token
        try {
            const licenseResponse = await API.getLicenseToken(extensionKey);
            await subscriptionStorage.setCachedLicense(extensionKey, {
                token: licenseResponse.license_token,
                entitlements: result,
                expires_at: licenseResponse.expires_at,
                last_synced: new Date().toISOString()
            });
        } catch (error) {
            console.warn('Failed to cache license token:', error);
            // Still return the entitlement result even if caching fails
        }

        return {
            ...result,
            offline: false
        };
    }

    /**
     * Check entitlements offline
     */
    async checkOffline(extensionKey, featureKey, quantity) {
        const cached = await subscriptionStorage.getCachedLicense(extensionKey);

        if (!cached || !cached.token) {
            return {
                has_subscription: false,
                allowed: false,
                reason: 'no_cached_license',
                offline: true
            };
        }

        // Validate token, binding it to the current device so a license cached
        // on one machine cannot be copied to another.
        const deviceId = await getDeviceId();
        const validation = validateLicenseToken(cached.token, deviceId);

        if (!validation.valid) {
            return {
                has_subscription: false,
                allowed: false,
                reason: validation.error || 'invalid_license_token',
                offline: true,
                expired: validation.expired,
                device_authorized: validation.deviceAuthorized !== false
            };
        }

        const payload = validation.payload;

        // Check if in grace period
        const now = new Date();
        const expiresAt = new Date(payload.expires_at);
        const graceEnd = new Date(payload.grace_period_end);

        if (now > graceEnd) {
            return {
                has_subscription: false,
                allowed: false,
                reason: 'license_expired',
                offline: true,
                grace_period_expired: true
            };
        }

        const inGracePeriod = validation.inGracePeriod;

        // Check feature entitlement if feature key provided
        if (featureKey) {
            const feature = payload.features && payload.features[featureKey];
            if (!feature || !feature.granted) {
                return {
                    allowed: false,
                    reason: 'feature_not_granted',
                    offline: true,
                    in_grace_period: inGracePeriod
                };
            }

            if (feature.type === 'limit' && feature.remaining !== null && feature.remaining !== undefined) {
                if (feature.remaining < quantity) {
                    return {
                        allowed: false,
                        reason: 'insufficient_quota',
                        remaining: feature.remaining,
                        offline: true,
                        in_grace_period: inGracePeriod
                    };
                }
            }
        }

        return {
            has_subscription: true,
            subscription_status: payload.status,
            subscription_id: payload.subscription_id,
            plan_id: payload.plan_id,
            allowed: true,
            reason: inGracePeriod ? 'grace_period' : 'ok',
            offline: true,
            in_grace_period: inGracePeriod,
            features: payload.features
        };
    }

    /**
     * Cache license token
     */
    async cacheLicense(extensionKey, entitlements) {
        try {
            const licenseResponse = await API.getLicenseToken(extensionKey);
            await subscriptionStorage.setCachedLicense(extensionKey, {
                token: licenseResponse.license_token,
                entitlements: entitlements,
                expires_at: licenseResponse.expires_at,
                last_synced: new Date().toISOString()
            });
        } catch (error) {
            console.error('Failed to cache license:', error);
            throw error;
        }
    }

    /**
     * Sync subscription status when online
     */
    async syncSubscription(extensionKey) {
        if (!this.isOnline()) {
            return { synced: false, reason: 'offline' };
        }

        try {
            const result = await this.checkOnline(extensionKey, '', 1);
            return { synced: true, result };
        } catch (error) {
            return { synced: false, error: error.message };
        }
    }

    /**
     * Start periodic sync for an extension
     */
    startPeriodicSync(extensionKey, interval = SYNC_INTERVAL) {
        // Stop existing sync if any
        this.stopPeriodicSync(extensionKey);

        // Start new sync interval
        const intervalId = setInterval(async () => {
            if (this.isOnline()) {
                try {
                    await this.syncSubscription(extensionKey);
                } catch (error) {
                    console.error(`Failed to sync subscription for ${extensionKey}:`, error);
                }
            }
        }, interval);

        this.syncIntervals.set(extensionKey, intervalId);
    }

    /**
     * Stop periodic sync for an extension
     */
    stopPeriodicSync(extensionKey) {
        const intervalId = this.syncIntervals.get(extensionKey);
        if (intervalId) {
            clearInterval(intervalId);
            this.syncIntervals.delete(extensionKey);
        }
    }

    /**
     * Stop all periodic syncs
     */
    stopAllSyncs() {
        this.syncIntervals.forEach((intervalId) => {
            clearInterval(intervalId);
        });
        this.syncIntervals.clear();
    }

    /**
     * Sync on network reconnect
     * Works for both web and desktop apps (Electron)
     */
    setupNetworkReconnectListener() {
        // Standard browser event (works in Electron too)
        window.addEventListener('online', () => {
            console.log('Network reconnected, syncing subscriptions...');
            // Sync all cached licenses
            this.syncAllCachedLicenses();
        });

        // For desktop apps, we can also listen to Electron's network status
        // if available via window.bodapro API
        if (window.bodapro && window.bodapro.platformType === 'desktop') {
            // Electron apps can use IPC to get network status
            // This is a placeholder - actual implementation would depend on Electron IPC setup
            if (window.bodapro.api && window.bodapro.api.listenOn) {
                window.bodapro.api.listenOn('network-status', (status) => {
                    if (status.online) {
                        console.log('Desktop app network reconnected, syncing subscriptions...');
                        this.syncAllCachedLicenses();
                    }
                });
            }
        }
    }

    /**
     * Sync all cached licenses
     */
    async syncAllCachedLicenses() {
        // Get all cached licenses and sync them
        // This is a simplified version - in production, you'd iterate through all cached licenses
        // For now, extensions should call syncSubscription individually
    }
}

// Export singleton instance
const offlineSubscriptionService = new OfflineSubscriptionService();
export default offlineSubscriptionService;

